import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import AdminLayout from '../../components/AdminLayout';

const SettingsContainer = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background-color: #007bff;
    color: white;

    &:hover {
      background-color: #0069d9;
    }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #6c757d;
    color: white;

    &:hover {
      background-color: #5a6268;
    }
  `}
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#cce7ff'};
  background-color: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#e3f2fd'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0d47a1'};
`;

const LinkText = styled.span`
  color: #007bff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SettingsPage = () => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(null);
  const navigate = useNavigate();

  const handlePasswordChange = (e) => {
    e.preventDefault();
    setPasswordStatus(null);

    if (!passwordData.currentPassword) {
      setPasswordStatus({ type: 'error', message: '请输入当前密码' });
      return;
    }

    if (!passwordData.newPassword) {
      setPasswordStatus({ type: 'error', message: '请输入新密码' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: '新密码长度不能少于6位' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordStatus({ type: 'error', message: '两次输入的新密码不一致' });
      return;
    }

    const confirmed = window.confirm('确定要修改密码吗？修改后请使用新密码登录。');
    if (!confirmed) {
      return;
    }

    localStorage.setItem('adminPassword', passwordData.newPassword);
    setPasswordStatus({ type: 'success', message: '密码修改成功！即将跳转到登录页面...' });
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

    setTimeout(() => {
      navigate('/admin/login');
    }, 1500);
  };

  return (
    <AdminLayout title="配置管理">
      <SettingsContainer>
        <Breadcrumb>
          <LinkText onClick={() => navigate('/admin/dashboard')}>仪表盘</LinkText> / 配置管理
        </Breadcrumb>

        <Header>
          <Title>配置管理</Title>
        </Header>

        <Section>
          <SectionTitle>快速链接</SectionTitle>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Button variant="primary" onClick={() => navigate('/admin/agent-config')}>
              智能体配置
            </Button>
          </div>
          <p style={{ marginTop: '1rem', color: '#666', fontSize: '0.9rem' }}>
            点击上方按钮进入智能体配置页面，配置DeepSeek API、服务时段、显示设置等。
          </p>
        </Section>

        <Section>
          <SectionTitle>修改管理员密码</SectionTitle>
          <form onSubmit={handlePasswordChange}>
            {passwordStatus && <Alert type={passwordStatus.type}>{passwordStatus.message}</Alert>}

            <FormGroup>
              <Label htmlFor="currentPassword">当前密码</Label>
              <Input
                type="password"
                id="currentPassword"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="请输入当前密码"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="newPassword">新密码</Label>
              <Input
                type="password"
                id="newPassword"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="请输入新密码（至少6位）"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">确认新密码</Label>
              <Input
                type="password"
                id="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="请再次输入新密码"
              />
            </FormGroup>

            <ButtonGroup>
              <Button type="submit" variant="primary">
                修改密码
              </Button>
              <Button type="button" variant="secondary" onClick={() => setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })}>
                重置
              </Button>
            </ButtonGroup>
          </form>
        </Section>
      </SettingsContainer>
    </AdminLayout>
  );
};

export default SettingsPage;
